import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AccountModelOption } from '../../../../../model/account-model-option.interface';
import { VehicleService } from '../../../../../service/vehicle.service';

@Component({
    selector: 'auto-advanced-general-settings',
    templateUrl: './advanced-general-settings.component.html',
    styleUrls: ['./advanced-general-settings.component.scss'],
    standalone: false
})
export class AdvancedGeneralSettingsComponent implements OnInit, OnChanges {
  @Input() data: any | null = null;
  @Input() accountId = 0;
  @Output() dataEmitter = new EventEmitter<UntypedFormGroup>();
  @Output() preCacheRequested = new EventEmitter<void>();
  form: UntypedFormGroup;
  focusModelOptions: AccountModelOption[] = [];
  focusModelOptionsLoading = false;
  private loadedModelOptionsAccountId: number | null = null;

  preCacheBusy = false;
  @Input() set preCacheBusyInput(value: boolean) {
    this.preCacheBusy = !!value;
  }

  oemProgramOptions = [
    { label: 'None', value: 'NONE' },
    { label: 'Acura Program', value: 'ACURA' },
    { label: 'Audi Program', value: 'AUDI' },
    { label: 'Ford Direct Program', value: 'FORD_DIRECT' },
    { label: 'Honda Program', value: 'HONDA' },
    { label: 'GM', value: 'GM' },
    { label: 'Stellantis Program', value: 'STELLANTIS_RTM' },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private vehicleService: VehicleService,
    private messageService: MessageService
  )
  {
  }

  ngOnInit(): void
  {
    this.form = this.formBuilder.group({
      preCacheSpecials: [false],
      emailVersion: [1],
      enhancedSeoEnabled: [false],
      oemProgramsSummary: [''],
      showDashboardReport: [false],
      adobeTaggingEnabled: [false],
      sharedOfferSetsEnabled: [false],
      dynamicCreativeEnabled: [false],
      maintainCreativeStatusForFocusModels: [false],
      focusModelIds: [[]],
      ipopEnabled: [false],
      mediaAdCopyBulkUpdateEnabled: [false],
      oemProgramSelect: ['NONE'],
      inAcuraProgram: [false],
      inAudiProgram: [false],
      inHondaProgram: [false],
      inGmProgram: [false],
      inFordDirectProgram: [false],
      fordDirectDealerId: [''],
      stellantisReportingTag: [false],
      fcaLastContacted: [''],
      fcaBusinessCenter: [''],
      hasTrimFeaturedSpecials: [false]
    });

    this.dataEmitter.emit(this.form);
    this.patchFromInput(this.data);

    this.form.get('oemProgramSelect')?.valueChanges.subscribe((val) => this.applyOemSelection(val));
    this.loadFocusModelOptions();

  }

  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['data']) {
      this.patchFromInput(changes['data'].currentValue);
    }

    if (changes['accountId'] && this.form) {
      this.loadFocusModelOptions();
    }
  }

  /**
   * Loads account-scoped Focus Model options once for the current account.
   */
  private loadFocusModelOptions(): void
  {
    const hasValidAccountId = Number.isInteger(this.accountId) && this.accountId > 0;
    const isNewAccount = this.loadedModelOptionsAccountId !== this.accountId;

    if (hasValidAccountId && isNewAccount) {
      this.loadedModelOptionsAccountId = this.accountId;
      this.focusModelOptions = [];
      this.focusModelOptionsLoading = true;
      this.vehicleService.getAccountModelOptions(this.accountId).subscribe({
        next: (options) => {
          this.focusModelOptions = this.normalizeFocusModelOptions(options);
          this.focusModelOptionsLoading = false;
        },
        error: () => {
          this.focusModelOptionsLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load Focus Model options.'
          });
        }
      });
    }
  }

  /**
   * Removes malformed options and keeps labels in deterministic order.
   */
  private normalizeFocusModelOptions(options: unknown): AccountModelOption[]
  {
    const optionsById = new Map<number, AccountModelOption>();

    if (Array.isArray(options)) {
      for (const option of options) {
        if (typeof option === 'object' && option !== null && 'id' in option && 'label' in option) {
          const modelId = Number(option.id);
          const modelLabel = option.label;
          if (Number.isInteger(modelId) && modelId > 0 && typeof modelLabel === 'string' && modelLabel !== '') {
            optionsById.set(modelId, {id: modelId, label: modelLabel});
          }
        }
      }
    }

    const normalizedOptions = Array.from(optionsById.values());
    normalizedOptions.sort((first, second) => first.label.localeCompare(second.label));

    return normalizedOptions;
  }

  /**
   * Normalizes persisted Focus Model IDs without relying on option-load order.
   */
  private normalizeFocusModelIds(modelIds: unknown): number[]
  {
    const normalizedModelIds = new Set<number>();

    if (Array.isArray(modelIds)) {
      for (const modelId of modelIds) {
        if (typeof modelId === 'number' && Number.isInteger(modelId) && modelId > 0) {
          normalizedModelIds.add(modelId);
        }
      }
    }

    const sortedModelIds = Array.from(normalizedModelIds);
    sortedModelIds.sort((first, second) => first - second);

    return sortedModelIds;
  }

  /** Keeps program enrollment mutually exclusive when the selection changes. */
  private applyOemSelection(selection: string): void
  {
    this.form.patchValue({
      inAcuraProgram: selection === 'ACURA',
      inAudiProgram: selection === 'AUDI',
      inHondaProgram: selection === 'HONDA',
      inGmProgram: selection === 'GM',
      inFordDirectProgram: selection === 'FORD_DIRECT',
      stellantisReportingTag: selection === 'STELLANTIS_RTM',
    }, { emitEvent: false });
  }

  /** Restores the selected program from persisted enrollment flags. */
  private deriveDropdownFromBooleans(general: Record<string, unknown>): string
  {
    const programMapping: { [key: string]: string } = {
      'stellantisReportingTag': 'STELLANTIS_RTM',
      'inHondaProgram': 'HONDA',
      'inGmProgram': 'GM',
      'inAudiProgram': 'AUDI',
      'inAcuraProgram': 'ACURA',
      'inFordDirectProgram': 'FORD_DIRECT'
    };

    let result = 'NONE';

    for (const [property, value] of Object.entries(programMapping)) {
      if (general?.[property] && result === 'NONE') {
        result = value;
      }
    }

    return result;
  }

  private patchFromInput(generalAdvancedSettings: any): void
  {
    const hasFormAndInput = !!generalAdvancedSettings && !!this.form;
    if (hasFormAndInput) {
      const oemSel = this.deriveDropdownFromBooleans(generalAdvancedSettings);

      this.form.patchValue({
        preCacheSpecials: !!generalAdvancedSettings.preCacheSpecials,
        emailVersion: Number(generalAdvancedSettings.emailToolVersion ?? 1),
        enhancedSeoEnabled: !!generalAdvancedSettings.enhancedSeoEnabled,
        showDashboardReport: !!generalAdvancedSettings.showDashboardReport,
        oemProgramSelect: oemSel,
        inGmProgram: !!generalAdvancedSettings.inGmProgram,
        adobeTaggingEnabled: !!generalAdvancedSettings.adobeTaggingEnabled,
        inAcuraProgram: !!(generalAdvancedSettings.inAcuraProgram ?? this.form.controls.inAcuraProgram.value ?? false),
        inAudiProgram: !!(generalAdvancedSettings.inAudiProgram ?? this.form.controls.inAudiProgram.value ?? false),
        inHondaProgram: !!(generalAdvancedSettings.inHondaProgram ?? this.form.controls.inHondaProgram.value ?? false),
        inFordDirectProgram: !!(generalAdvancedSettings.inFordDirectProgram ?? this.form.controls.inFordDirectProgram.value ?? false),
        fordDirectDealerId: generalAdvancedSettings.fordDirectDealerId ?? this.form.controls.fordDirectDealerId.value ?? '',
        stellantisReportingTag: !!(generalAdvancedSettings.stellantisReportingTag ?? this.form.controls.stellantisReportingTag.value ?? false),
        dynamicCreativeEnabled: !!generalAdvancedSettings.dynamicCreativeEnabled,
        maintainCreativeStatusForFocusModels: !!generalAdvancedSettings.maintainCreativeStatusForFocusModels,
        focusModelIds: this.normalizeFocusModelIds(generalAdvancedSettings.focusModelIds),
        ipopEnabled: !!generalAdvancedSettings.ipopEnabled,
        mediaAdCopyBulkUpdateEnabled: !!generalAdvancedSettings.mediaAdCopyBulkUpdateEnabled,
      }, {emitEvent: false});

      this.form.markAsPristine();
    }
  }

  get preCacheLabel(): string {
    let label = 'Refresh Ad Cache';

    if (this.preCacheBusy) {
      label = 'Refreshing Ad Cache...';
    }

    return label;
  }

  get preCacheIcon(): string {
    let icon = 'pi pi-refresh';

    if (this.preCacheBusy) {
      icon = 'pi pi-refresh pi-spin';
    }

    return icon;
  }

  onPreCacheClick(): void {
    if (!this.preCacheBusy) {
      this.preCacheRequested.emit();
    }
  }
}
